import * as React from "react";
import { Link, useParams } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Box, Typography, IconButton, Skeleton } from "@mui/material";
import { ApolloError, useQuery } from "@apollo/client";
import { ContainerCardLayout } from "components/haven-design";
import {
  DocumentDetails as Query,
  DocumentDetailsVariables as Vars,
} from "types/gql-types";
import { formatUploadDate } from "../utls";
import { documentQuery as query } from "./queries";
// styles removed in favor of MUI components

type DocumentData = Query["document"];

type DocumentTitleProps = { loading: boolean; category?: string };

const DocumentTitle: React.FC<DocumentTitleProps> = ({
  loading,
  category,
}) => (
  <Typography variant="h5">
    <Link to="/dash/client/documents">My Documents</Link>
    {" "}&gt; {" "}
    {loading ? (
      <Skeleton variant="text" width={180} sx={{ display: "inline-block", verticalAlign: "middle" }} />
    ) : (
      category
    )}
  </Typography>
);

const DocumentBody: React.FC<{
  loading: boolean;
  errorMessage?: string;
  document?: DocumentData;
}> = ({ loading, errorMessage, document }) => (
  <div style={{ margin: 16 }}>
    {errorMessage && (
      <Typography color="error">{errorMessage}</Typography>
    )}

    {!loading && !document && !errorMessage && (
      <Typography color="text.secondary">Document failed to load.</Typography>
    )}

    {loading && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={320} height={32} />
          <Skeleton variant="text" width={180} />
        </Box>
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    )}

    {!loading && document && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">
            <a href={document.url} target="_blank" rel="noreferrer">
              {document.name}
            </a>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatUploadDate(document.uploadedAt)}
          </Typography>
        </Box>
        <IconButton component="a" href={document.downloadUrl} color="primary" aria-label="download">
          <Icon>download</Icon>
        </IconButton>
      </Box>
    )}
  </div>
);

enum QueryErrors {
  DocumentNotFound = "document_not_found",
  NotAuthorized = "not_authorized",
}

const handleApolloError = (e: ApolloError) => {
  switch (e.message) {
    case QueryErrors.DocumentNotFound:
      return "Invalid document ID";
    case QueryErrors.NotAuthorized:
      return "Not authorized";
    default:
      return `An unknown error occured - ${e.message}`;
  }
};

const Document: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<Query, Vars>(query, { variables: { id: id ?? "" } });
  const document = data?.document;

  return (
    <ContainerCardLayout title={<DocumentTitle loading={loading} category={document?.category} />}>
      <DocumentBody
        loading={loading}
        document={document}
        {...(error ? { errorMessage: handleApolloError(error) } : {})}
      />
    </ContainerCardLayout>
  );
};

export default Document;
